import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  rebuildRoutePeriods,
  syncRouteTemplateFutureJobs,
} from "../../api/routeTemplates";

// Local calendar date (the user's "today"), not UTC.
export function todayLocalDateKey() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const DEFAULT_LOOKAHEAD_DAYS = 14;

function formatDateTime(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDay(period) {
  const source = period.day_key || period.period_start;
  if (!source) return null;
  const date = new Date(period.day_key ? `${period.day_key}T12:00:00` : source);
  if (Number.isNaN(date.getTime())) return String(source);
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
}

function jobLabel(job) {
  const place = job.room_name || job.device_name;
  return [job.job_number || `Job #${job.job_id}`, place].filter(Boolean).join(" · ");
}

function summaryLine(result) {
  if (!result?.summary) return null;
  const s = result.summary;
  const parts = [];
  if (s.jobs_to_move) parts.push(`${s.jobs_to_move} job(s) moved between periods`);
  if (s.unplaced_jobs) parts.push(`${s.unplaced_jobs} unplaced job(s) placed`);
  if (s.periods_to_change) parts.push(`${s.periods_to_change} period(s) re-ordered`);
  if (s.periods_to_create) parts.push(`${s.periods_to_create} new period(s)`);
  if (s.blocked_jobs) parts.push(`${s.blocked_jobs} job(s) held back`);
  return parts.length > 0 ? parts.join(", ") : "Assignment periods already match the routes.";
}

function JobList({ title, jobs, highlightIds, highlightColor, leavingTo }) {
  const highlight = new Set((highlightIds || []).map(String));
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
        {jobs.map((job, index) => {
          const isHighlighted = highlight.has(String(job.job_id));
          const destination = leavingTo?.find((l) => String(l.job_id) === String(job.job_id));
          return (
            <Box
              key={job.job_id}
              sx={{
                display: "flex",
                gap: 0.75,
                alignItems: "baseline",
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                bgcolor: isHighlighted ? highlightColor : "transparent",
                textDecoration: job.status === "cancelled" ? "line-through" : "none",
                color: job.status === "cancelled" ? "text.disabled" : "text.primary",
              }}
            >
              <Typography variant="caption" sx={{ width: 18, flexShrink: 0, fontWeight: 700 }}>
                {index + 1}
              </Typography>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                {jobLabel(job)}
                {job.route_code && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {` (${job.route_name || job.route_code})`}
                  </Typography>
                )}
              </Typography>
              {destination && (
                <Typography variant="caption" color="error.main" sx={{ flexShrink: 0 }}>
                  {destination.to_new_period
                    ? "→ new period"
                    : destination.to_period_id
                      ? `→ period #${destination.to_period_id}`
                      : "→ moves"}
                </Typography>
              )}
            </Box>
          );
        })}
        {jobs.length === 0 && (
          <Typography variant="body2" color="text.disabled">
            No jobs
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function ManualPeriodCard({ period, decision, onDecision }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {period.label || `Period #${period.assignment_period_id}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {[
              formatDay(period),
              period.assigned_username ? `Assigned to ${period.assigned_username}` : null,
              `Edited by hand${
                period.manually_modified_by_username
                  ? ` by ${period.manually_modified_by_username}`
                  : ""
              }${period.manually_modified_at ? ` · ${formatDateTime(period.manually_modified_at)}` : ""}`,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: "wrap", gap: 0.5 }}>
            {period.is_merged && <Chip size="small" label="Merged" variant="outlined" />}
            {period.reordered_only ? (
              <Chip size="small" label="Order changes" color="info" variant="outlined" />
            ) : (
              <>
                {period.jobs_joining?.length > 0 && (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label={`${period.jobs_joining.length} job(s) join`}
                  />
                )}
                {period.jobs_leaving?.length > 0 && (
                  <Chip
                    size="small"
                    color="error"
                    variant="outlined"
                    label={`${period.jobs_leaving.length} job(s) leave`}
                  />
                )}
              </>
            )}
            {period.order_basis === "master" && (
              <Chip size="small" variant="outlined" label="Master order (several routes)" />
            )}
          </Stack>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={decision}
          onChange={(_, value) => value && onDecision(value)}
        >
          <ToggleButton value="ignore">Ignore</ToggleButton>
          <ToggleButton value="process" color="primary">
            Process
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
        <JobList
          title="Now"
          jobs={period.current_jobs || []}
          highlightIds={period.jobs_leaving}
          highlightColor="rgba(211, 47, 47, 0.08)"
          leavingTo={period.jobs_leaving_to}
        />
        <JobList
          title="After processing"
          jobs={period.proposed_jobs || []}
          highlightIds={period.jobs_joining}
          highlightColor="rgba(46, 125, 50, 0.10)"
        />
      </Box>
    </Paper>
  );
}

/**
 * One dialog for both:
 *  - mode "sync":    apply route template changes to future jobs (plan sync)
 *                    and rebuild assignment periods, from a chosen day;
 *  - mode "rebuild": only rebuild assignment periods, from a chosen day.
 *
 * Manually edited periods are never changed silently: they are listed with
 * their current and proposed job order, and the user decides per period to
 * process (apply the route order) or ignore (leave it as edited).
 */
export default function RoutePeriodSyncDialog({ open, mode = "sync", templateId, onClose }) {
  const [step, setStep] = useState("options"); // options | review | done
  const [useSpecificDate, setUseSpecificDate] = useState(false);
  const [fromDate, setFromDate] = useState(todayLocalDateKey());
  const [lookaheadDays, setLookaheadDays] = useState(DEFAULT_LOOKAHEAD_DAYS);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [syncSummary, setSyncSummary] = useState(null);
  const [rebuildResult, setRebuildResult] = useState(null);
  const [finalResult, setFinalResult] = useState(null);
  const [decisions, setDecisions] = useState({});

  useEffect(() => {
    if (!open) return;
    setStep("options");
    setUseSpecificDate(false);
    setFromDate(todayLocalDateKey());
    setLookaheadDays(DEFAULT_LOOKAHEAD_DAYS);
    setRunning(false);
    setError(null);
    setWarning(null);
    setSyncSummary(null);
    setRebuildResult(null);
    setFinalResult(null);
    setDecisions({});
  }, [open, mode]);

  const effectiveFromDate = useSpecificDate ? fromDate : todayLocalDateKey();
  const pendingManual = useMemo(
    () => rebuildResult?.requires_confirmation || [],
    [rebuildResult],
  );
  const processIds = pendingManual
    .filter((p) => decisions[p.assignment_period_id] === "process")
    .map((p) => p.assignment_period_id);

  // completed: jobs were synced and periods rebuilt (manual periods may
  // still have been ignored). If the rebuild could not run, the page keeps
  // its "sync needed" notice.
  const close = () => {
    if (running) return;
    const completed = step !== "options" && rebuildResult != null;
    onClose?.({
      completed,
      message: step !== "options" ? buildCompletionMessage() : null,
      ignoredManualPeriods: pendingManual.length - (finalResult ? processIds.length : 0),
    });
  };

  function buildCompletionMessage() {
    const parts = [];
    if (syncSummary) parts.push(syncSummary);
    const rebuilt = finalResult || rebuildResult;
    if (rebuilt) parts.push(summaryLine(rebuilt));
    const ignored = pendingManual.length - processIds.length;
    if (finalResult && processIds.length > 0) {
      parts.push(`${processIds.length} manually edited period(s) processed`);
    }
    if (ignored > 0) {
      parts.push(`${ignored} manually edited period(s) left as they are`);
    }
    return `${mode === "sync" ? "Sync" : "Rebuild"} from ${effectiveFromDate} complete. ${parts.join(". ")}.`;
  }

  const handleRun = async () => {
    if (!templateId) return;
    if (useSpecificDate && !fromDate) {
      setError("Choose a date to sync from.");
      return;
    }
    setRunning(true);
    setError(null);
    setWarning(null);

    const payload = {
      effective_from_date: effectiveFromDate,
      lookahead_days: Number(lookaheadDays) > 0 ? Number(lookaheadDays) : DEFAULT_LOOKAHEAD_DAYS,
    };

    try {
      let periodRebuild;
      if (mode === "sync") {
        const response = await syncRouteTemplateFutureJobs(templateId, payload);
        const synced = response?.sync?.synced_plans ?? 0;
        const requested = response?.sync?.requested_plans ?? 0;
        setSyncSummary(`Future jobs synced for ${synced}/${requested} plan(s)`);
        if (requested > synced) {
          setWarning(
            `${requested - synced} plan(s) could not be synced. The automation service may be unavailable.`,
          );
        }
        periodRebuild = response?.period_rebuild;
      } else {
        const response = await rebuildRoutePeriods(templateId, payload);
        periodRebuild = response?.period_rebuild;
      }

      if (!periodRebuild?.success) {
        setWarning(
          (prev) =>
            `${prev ? `${prev} ` : ""}Assignment periods were not rebuilt: ${
              periodRebuild?.error || "the automation service did not respond"
            }.`,
        );
        setStep("done");
        return;
      }

      const result = periodRebuild.result;
      setRebuildResult(result);
      const manual = result?.requires_confirmation || [];
      if (manual.length > 0) {
        setDecisions(
          Object.fromEntries(manual.map((p) => [p.assignment_period_id, "ignore"])),
        );
        setStep("review");
      } else {
        setStep("done");
      }
    } catch (err) {
      setError(err.message || "Failed to run");
    } finally {
      setRunning(false);
    }
  };

  const handleApplyDecisions = async () => {
    if (processIds.length === 0) {
      setStep("done");
      return;
    }
    setRunning(true);
    setError(null);
    try {
      const response = await rebuildRoutePeriods(templateId, {
        effective_from_date: effectiveFromDate,
        lookahead_days: Number(lookaheadDays) > 0 ? Number(lookaheadDays) : DEFAULT_LOOKAHEAD_DAYS,
        confirm_period_ids: processIds,
      });
      setFinalResult(response?.period_rebuild?.result || null);
      setStep("done");
    } catch (err) {
      setError(
        err.code === "REBUILD_CONFLICT"
          ? "Some assignment periods changed while processing. Check the list and try again."
          : err.message || "Failed to process the selected periods",
      );
    } finally {
      setRunning(false);
    }
  };

  const setAll = (value) =>
    setDecisions(Object.fromEntries(pendingManual.map((p) => [p.assignment_period_id, value])));

  const title =
    mode === "sync" ? "Sync route changes" : "Rebuild assignment periods";

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth={step === "review" ? "md" : "xs"}
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {warning && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {warning}
          </Alert>
        )}

        {step === "options" && (
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {mode === "sync"
                ? "Apply the route changes to existing future jobs and re-order their assignment periods to the new route order."
                : "Re-order assignment periods to the current route order, and move jobs whose room has changed route."}{" "}
              Periods that have started are never changed. Periods edited by hand are
              shown to you before anything changes.
            </Typography>
            <RadioGroup
              value={useSpecificDate ? "date" : "today"}
              onChange={(event) => setUseSpecificDate(event.target.value === "date")}
            >
              <FormControlLabel value="today" control={<Radio size="small" />} label="From today" />
              <FormControlLabel
                value="date"
                control={<Radio size="small" />}
                label="From a specific day"
              />
            </RadioGroup>
            {useSpecificDate && (
              <TextField
                label="Sync from"
                type="date"
                size="small"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            )}
            <TextField
              label="Days ahead"
              type="number"
              size="small"
              value={lookaheadDays}
              onChange={(event) => setLookaheadDays(event.target.value)}
              inputProps={{ min: 1, max: 90 }}
              helperText={`Covers ${effectiveFromDate} and the following ${Math.max(
                0,
                Number(lookaheadDays || 0) - 1,
              )} day(s).`}
            />
          </Stack>
        )}

        {step === "review" && (
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {syncSummary && (
              <Typography variant="body2" color="text.secondary">
                {syncSummary}.
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {summaryLine(rebuildResult)}
            </Typography>
            <Alert severity="info">
              {pendingManual.length} assignment period(s) were edited by hand and have
              not been changed. Choose <b>Process</b> to apply the route order to a
              period (your manual changes to it are replaced), or <b>Ignore</b> to keep
              it as it is.
            </Alert>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button size="small" onClick={() => setAll("ignore")} disabled={running}>
                Ignore all
              </Button>
              <Button size="small" onClick={() => setAll("process")} disabled={running}>
                Process all
              </Button>
            </Box>
            {pendingManual.map((period) => (
              <ManualPeriodCard
                key={period.assignment_period_id}
                period={period}
                decision={decisions[period.assignment_period_id] || "ignore"}
                onDecision={(value) =>
                  setDecisions((prev) => ({ ...prev, [period.assignment_period_id]: value }))
                }
              />
            ))}
          </Stack>
        )}

        {step === "done" && (
          <Stack spacing={1} sx={{ mt: 0.5 }}>
            {syncSummary && <Typography variant="body2">{syncSummary}.</Typography>}
            {(finalResult || rebuildResult) && (
              <Typography variant="body2">{summaryLine(finalResult || rebuildResult)}</Typography>
            )}
            {finalResult && processIds.length > 0 && (
              <Typography variant="body2">
                {processIds.length} manually edited period(s) processed.
              </Typography>
            )}
            {pendingManual.length - processIds.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {pendingManual.length - processIds.length} manually edited period(s) left
                as they are. You can process them later with "Rebuild periods".
              </Typography>
            )}
            {(rebuildResult?.summary?.blocked_jobs || 0) > 0 && (
              <Typography variant="body2" color="text.secondary">
                Jobs in periods that have already started were not moved.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {step === "options" && (
          <>
            <Button onClick={close} disabled={running}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleRun} disabled={running}>
              {running ? (
                <CircularProgress size={18} />
              ) : mode === "sync" ? (
                "Sync now"
              ) : (
                "Rebuild now"
              )}
            </Button>
          </>
        )}
        {step === "review" && (
          <Button variant="contained" onClick={handleApplyDecisions} disabled={running}>
            {running ? (
              <CircularProgress size={18} />
            ) : processIds.length > 0 ? (
              `Process ${processIds.length} period(s)`
            ) : (
              "Keep all as they are"
            )}
          </Button>
        )}
        {step === "done" && (
          <Button variant="contained" onClick={close}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
